import { IconGooglePhotos } from "assets/img/icons";
import Axios from "axios";
import { googlePhotos as googlePhotosConfig } from "config";
import { STATUS_CODE } from "constants/APIs";
import { MEDIA_MAX_DIMENSIONS, UPLOADER_TYPE } from "constants/common";
import errorMsgs from "constants/errorMsgs";
import { GOOGLE, GOOGLE_PICKER } from "constants/widgetBuilder";
import WidgetBuilderContext from "contexts/WidgetBuilder";
import loadScript from "load-script";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { validateOAuthGoogleToken } from "services/funcHelper";
import utils from "services/utils";
import { popupWindow } from "services/widgetBuilder";
import { storeData } from "stores/actions";
import { StyledSourceIcon } from "styled-components/WidgetBuilder/WidgetUploadSourceStyled";

const GooglePhotos = (props) => {
	const { t } = useTranslation();
	const { setGoogleAppData, activeUploaderType, widgetInfo, handleErrorMsg } = props;
	const { widgetSettingsDTO = {} } = widgetInfo || {};

	const BUTTON_ID = "google_photos_picker_btn";
	const dispatch = useDispatch();
	const { googlePhotosDetails } = useSelector((state) => state.widgets);
	const intervalRef = useRef(null);

	const [tokenClient, setTokenClient] = useState(null);

	useEffect(() => {
		loadScript(GOOGLE.GIS_SCRIPT, gisLoaded);
	}, []);

	useEffect(() => {
		if (activeUploaderType !== UPLOADER_TYPE.GOOGLE_PHOTOS) {
			stopInterval();
		}
	}, [activeUploaderType]);

	useEffect(() => {
		// googlePhotosDetails?.accessToken is different from googleAccessToken
		// (googlePhotosDetails?.accessToken is used for Google Photos only)
		// (googleAccessToken is used for Google Drive)
		if (
			!googlePhotosDetails?.sessionId ||
			!googlePhotosDetails?.accessToken ||
			activeUploaderType !== UPLOADER_TYPE.GOOGLE_PHOTOS
		)
			return;

		intervalRef.current = setInterval(() => {
			getSelectedMediaItems();
		}, 3000);

		return () => stopInterval();
	}, [googlePhotosDetails, activeUploaderType]);

	const stopInterval = () => {
		if (intervalRef.current) {
			clearInterval(intervalRef.current);
			intervalRef.current = null;
		}
	};

	const gisLoaded = () => {
		if (!window.google?.accounts?.oauth2) {
			console.error("Google Identity Services not initialized.");
			return;
		}

		const client = window.google.accounts.oauth2.initTokenClient({
			client_id: googlePhotosConfig.clientId,
			scope: GOOGLE_PICKER.PHOTOS_SCOPES,
			callback: handleTokenResponse,
		});

		setTokenClient(client);
	};

	const handleTokenResponse = (tokenResponse) => {
		handlePickerSession(tokenResponse.access_token);
	};

	const refreshGooglePhotosState = () => {
		dispatch(
			storeData({
				googlePhotosDetails: {
					...googlePhotosDetails,
					pickerUri: null,
					sessionId: null,
				},
			}),
		);
		if (googlePhotosDetails?.accessToken) {
			handlePickerSession(googlePhotosDetails?.accessToken, null);
		}
	};

	const createSession = async (token) => {
		return Axios.post(
			GOOGLE.PHOTOS_PICKER_SESSION_API,
			{
				pickingConfig: {
					maxItemCount: 20,
				},
			},
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		);
	};

	const getSession = async (token, sessionId) => {
		return Axios.get(`${GOOGLE.PHOTOS_PICKER_SESSION_API}/${sessionId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		});
	};

	const handlePickerSession = async (token, sessionId) => {
		try {
			const res = sessionId ? await getSession(token, sessionId) : await createSession(token);

			const { status, data } = res;

			if (status === STATUS_CODE.SUCCESS && data?.pickerUri && data?.id) {
				popupWindow(data.pickerUri, `Google Photos Picker ${new Date().getTime()}`);

				dispatch(
					storeData({
						googlePhotosDetails: {
							accessToken: token,
							pickerUri: data.pickerUri,
							sessionId: data.id,
						},
					}),
				);
			} else {
				console.warn("Unexpected session response", res);
				refreshGooglePhotosState();
			}
		} catch (error) {
			console.error("Error handling session", error);
			refreshGooglePhotosState();
		}
	};

	const getSelectedMediaItems = async () => {
		if (!googlePhotosDetails?.accessToken) return;

		try {
			const res = await Axios.get(
				`${GOOGLE.PHOTOS_PICKER_MEDIA_ITEMS_API}?sessionId=${googlePhotosDetails?.sessionId}`,
				{
					headers: {
						Authorization: `Bearer ${googlePhotosDetails?.accessToken}`,
					},
				},
			);

			if (res.data.mediaItems) {
				// Convert files which are suitable for the available validating function
				const files = res.data.mediaItems?.map((item) => {
					return {
						...item,
						type: item?.mediaFile?.mimeType,
						uploadType: UPLOADER_TYPE.GOOGLE_PHOTOS,
					};
				});

				const { errorMsg, isValidFiles } = await utils.validateUploadedFiles(
					files,
					"en",
					widgetSettingsDTO,
					true,
				);

				if (errorMsg && !isValidFiles) {
					handleErrorMsg(errorMsg);
					return;
				}

				const filterMedias = files.map((item) => {
					let limitationError = {};
					const { filename } = item.mediaFile || {};
					const { width, height } = item.mediaFile.mediaFileMetadata || {};
					if (width > MEDIA_MAX_DIMENSIONS.W || height > MEDIA_MAX_DIMENSIONS.H) {
						limitationError = { msg: errorMsgs["en"].invalidFilesize };
					}

					return {
						...item,
						fileId: item.id,
						fileName: filename,
						limitationError,
					};
				});

				setGoogleAppData({
					selectedMediaList: filterMedias,
					accessToken: googlePhotosDetails?.accessToken,
				});
				stopInterval();
			}
		} catch (err) {
			console.error(
				"Failed to fetch selected media items from Google Photos. Please try again.",
				err,
			);
		}
	};

	const onKeyDown = () => {
		const el = document.getElementById(BUTTON_ID);
		if (el) {
			el.click();
		}
	};

	const signIn = async () => {
		if (!tokenClient) {
			console.error("Token client is not initialized.");
			return;
		}

		tokenClient.requestAccessToken({
			include_granted_scopes: false, // DO NOT include previously granted scopes
		});
	};

	const onClickGoogleDriveBtn = async () => {
		stopInterval();
		if (googlePhotosDetails?.accessToken) {
			const isValidToken = await validateOAuthGoogleToken(googlePhotosDetails?.accessToken);
			if (isValidToken) {
				handlePickerSession(googlePhotosDetails?.accessToken, googlePhotosDetails?.sessionId);
			} else {
				signIn();
			}
		} else {
			signIn();
		}
	};

	return (
		<StyledSourceIcon
			id={BUTTON_ID}
			tabIndex={0}
			cursor="pointer"
			size={37}
			onKeyDown={(e) => {
				utils.onKeyDown(e, onKeyDown);
			}}
			onClick={onClickGoogleDriveBtn}>
			<IconGooglePhotos />
		</StyledSourceIcon>
	);
};

export default React.memo(GooglePhotos);
